import {Stack} from '@mui/material';
import {get, isEqual} from 'lodash';
import {FC, Fragment, MouseEvent, useEffect, useRef} from 'react';
import {useStore} from 'zustand';
import {useDebounce} from '../../../../../../hooks/useDebounce';
import {PERIOD_NAMES} from '../../constants/period';
import {renderPeriodValue} from '../../helpers';
import {useDashboardServiceRequestsTableData} from '../../hooks/useDashboardServiceRequestsTableData';
import {useStoresContext} from '../../hooks/useStoresContext';
import {
  resetDashboardServiceRequestsTablePagination,
  setDashboardServiceRequestsTablePage,
  setDashboardServiceRequestsTableSize,
  setDashboardServiceRequestsTableSort,
  useDashboardServiceRequestsTableStore,
} from '../../store/useDashboardRequestsTableStore';
import {DashboardArchiveCheckbox} from '../DashboardArchiveCheckbox';
import {DashboardControlButtons} from '../DashboardControlButtons';
import {DashboardServiceRequestStatusFilters} from '../DashboardServiceRequestStatusFilters';
import {DashboardServiceRequestsFilters} from '../DashboardServiceRequestsFilters/DashboardServiceRequestsFilters';
import {PeriodFilterTooltip} from '../PeriodFilterTooltip';
import {PeriodPicker, PeriodPickerRef} from '../PeriodPicker';
import {ServiceRequestByDateStatusLegend} from '../ServiceRequestByDateStatusLegend';
import {ServiceRequestsDashboardTable} from '../ServiceRequestsDashboardTable';
import {requestStatusesStyles as STYLES} from './RequestStatusesDashboardCard.styles';

interface Props {
  serviceType: string;
}

export const RequestsStatusesDashboardCard: FC<Props> = ({serviceType}) => {
  const {
    requestsPeriodFiltersStore,
    serviceRequestsArchiveFilterStore,
    dashboardServiceRequestsFiltersStore,
    serviceRequestsStatusFilterStore,
  } =
    useStoresContext(serviceType);
  const {data, loading} = useDashboardServiceRequestsTableData(serviceType);

  const {page, size} = useDashboardServiceRequestsTableStore();

  const {period, setPeriod, customPeriodEnd, customPeriodStart, setCustomPeriod, resetCustomPeriod} =
    useStore(requestsPeriodFiltersStore);
  const archived = useStore(serviceRequestsArchiveFilterStore, (s) => s.archived);
  const {searchValue, searchBy, filters} = useStore(dashboardServiceRequestsFiltersStore);
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const {statusFilter} = useStore(serviceRequestsStatusFilterStore);
  const periodPickerRef = useRef<PeriodPickerRef>(null);

  useEffect(() => {
    resetDashboardServiceRequestsTablePagination();
  }, [serviceType, period, customPeriodStart, customPeriodEnd, archived, debouncedSearchValue, searchBy, filters, statusFilter]);

  const handleCustomPeriodChange = (start: string, end: string) => {
    setCustomPeriod(start, end);
    setPeriod(PERIOD_NAMES.MANUAL);
  };

  const handlePeriodClick = (e: MouseEvent<HTMLDivElement>) => {
    const value = get(e, 'target.dataset.value');
    if (!value) return;
    if (isEqual(value, PERIOD_NAMES.MANUAL)) {
      periodPickerRef.current?.open();
      return;
    }
    setPeriod(value);
    resetCustomPeriod();
    periodPickerRef.current?.reset();
  };

  return (
    <Fragment>
      <DashboardServiceRequestsFilters serviceType={serviceType} />

      <Stack direction="row" alignItems="center" sx={STYLES.header}>
        <PeriodFilterTooltip
          title={renderPeriodValue(customPeriodStart, customPeriodEnd)(period)}
          period={period}
          onClick={handlePeriodClick}
          renderValue={renderPeriodValue(customPeriodStart, customPeriodEnd)}
        />

        <DashboardServiceRequestStatusFilters serviceType={serviceType} />

        <Stack direction="row" alignItems="center" sx={STYLES.typeButtons}>
          <DashboardArchiveCheckbox />

          <DashboardControlButtons serviceType={serviceType} />
        </Stack>
      </Stack>

      <ServiceRequestByDateStatusLegend />

      <ServiceRequestsDashboardTable
        name="contracts-table"
        loading={loading}
        pageSize={size}
        rows={data?.content || []}
        currentPage={page}
        totalPages={data?.numberOfElements === 0 ? 0 : (data?.totalPages ?? 0)}
        startPageFromZero
        totalCount={data?.numberOfElements === 0 ? 0 : data?.totalElements}
        serviceType={serviceType}
        onSortModelChange={setDashboardServiceRequestsTableSort}
        onPageSizeChange={setDashboardServiceRequestsTableSize}
        onPaginationChange={setDashboardServiceRequestsTablePage}
      />

      <PeriodPicker ref={periodPickerRef} onAccept={handleCustomPeriodChange} />
    </Fragment>
  );
};
